import { useCallback, useEffect, useRef, useState } from 'react';

import { analyzeChartFeedbackPeak } from '@/entities/chart';
import { analyzeFeedbackPeak } from '@/entities/feedback';

import type {
  FeedbackPeakAnalysisOptions,
  FeedbackPeakAnalysisRequest,
} from './peak-analysis';
import {
  initialPeakAnalysisState,
  type PeakAnalysisState,
} from './peak-analysis-panel';

const buildDefaultFilterRequest = (): FeedbackPeakAnalysisRequest => ({
  type: 'filter',
});

const runPeakAnalysisRequest = (
  peakAnalysisOptions: FeedbackPeakAnalysisOptions,
  request: FeedbackPeakAnalysisRequest = buildDefaultFilterRequest(),
) => {
  switch (request.type) {
    case 'filter':
      return analyzeFeedbackPeak(
        request.params ?? {
          filterSet: peakAnalysisOptions.filterSet,
          frequency: peakAnalysisOptions.frequency,
        },
      );
    case 'chart-cell':
      return analyzeChartFeedbackPeak(request.workspaceAlias, request.params);
    default: {
      const exhaustiveCheck: never = request;
      throw new Error(`Unsupported peak analysis request: ${exhaustiveCheck}`);
    }
  }
};

export const usePeakAnalysis = (
  peakAnalysisOptions?: FeedbackPeakAnalysisOptions,
  feedbackAlias?: string,
) => {
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(false);
  const [state, setState] = useState<PeakAnalysisState>(
    initialPeakAnalysisState,
  );
  const isPeakAnalysisLoading = state.status === 'loading';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    setState(initialPeakAnalysisState);
  }, [feedbackAlias, peakAnalysisOptions]);

  const startPeakAnalysis = useCallback(
    async (request?: FeedbackPeakAnalysisRequest) => {
      if (!peakAnalysisOptions || isPeakAnalysisLoading) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setState({ status: 'loading' });

      try {
        const res = await runPeakAnalysisRequest(peakAnalysisOptions, request);

        if (!isMountedRef.current || requestIdRef.current !== requestId) {
          return;
        }

        setState({
          status: 'success',
          text: res.text,
        });
      } catch {
        if (!isMountedRef.current || requestIdRef.current !== requestId) {
          return;
        }

        setState({
          status: 'error',
          text: 'Unable to analyze change drivers. Please try again shortly.',
        });
      }
    },
    [feedbackAlias, isPeakAnalysisLoading, peakAnalysisOptions],
  );

  return {
    isPeakAnalysisLoading,
    peakAnalysisState: state,
    startPeakAnalysis,
  };
};
